"use client";

import { Search, Bell, User, LogOut, Settings, BadgeDollarSign, List, Package, Wallet, MessageSquare, AlertCircle } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { signOut, useSession } from "next-auth/react";
import { Badge } from "@/components/ui/badge";
import { useRouter } from "next/navigation";
import moment from "moment";

interface Notification {
    _id: string;
    message: string;
    type: "system" | "order" | "wallet" | "support";
    priority: "high" | "medium" | "low";
    isRead: boolean;
    createdAt: string;
}

interface DashboardNavbarProps {
    notifications?: Notification[];
    walletBalance?: number;
}

const DashboardNavbar = ({ notifications = [], walletBalance = 0 }: DashboardNavbarProps) => {
    const [searchActive, setSearchActive] = useState(false);
    const { data: session } = useSession()
    const router = useRouter()

    return (
        <div className="relative w-full px-6 py-4 hidden md:flex">
            <div className="flex justify-between items-center w-full rounded-lg shadow-lg transition-all duration-500">

                <motion.div
                    className={cn(
                        "relative flex items-center px-4 transition-all duration-500 rounded-xl border border-white/10 backdrop-blur-lg bg-white/10",
                        searchActive ? "w-72" : "w-52"
                    )}
                    whileHover={{ scale: 1.02 }}
                >
                    <Search className="text-gray-300 w-5 h-5" />
                    <Input
                        type="text"
                        placeholder="جستجو..."
                        className="bg-transparent border-none outline-none ml-2 text-white placeholder-gray-400"
                        onFocus={() => setSearchActive(true)}
                        onBlur={() => setSearchActive(false)}
                    />
                </motion.div>

                <div className="flex items-center gap-3">

                    <Popover>
                        <PopoverTrigger asChild>
                            <motion.div
                                whileTap={{ scale: 0.9 }}
                                whileHover={{ scale: 1.1 }}
                                className="relative cursor-pointer p-2 rounded-xl bg-white/10 backdrop-blur-lg border border-white/10"
                            >
                                <Bell className="text-gray-300 w-5 h-5" />
                                {notifications?.filter(n => !n.isRead).length > 0 && (
                                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>
                                )}
                            </motion.div>
                        </PopoverTrigger>
                        <PopoverContent
                            side="bottom"
                            align="center"
                            sideOffset={10}
                            className="w-80 bg-[#1A1525]/60 backdrop-blur-xl border border-white/10 text-white shadow-xl shadow-purple-500/10 rounded-xl p-4"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-semibold flex items-center gap-2"><Bell className="w-5 h-5" /> اعلان‌ها</h3>
                                <button 
                                    onClick={() => router.push('/dashboard/notifications')}
                                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                                >
                                    مشاهده همه
                                </button>
                            </div>
                            <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                {notifications?.length > 0 ? (
                                    notifications.map((notification) => (
                                        <div
                                            key={notification._id}
                                            className={`p-3 rounded-lg transition-all duration-300 ${
                                                notification.isRead 
                                                    ? "bg-white/5" 
                                                    : "bg-white/10 border border-white/20"
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className="text-gray-300">
                                                    {notification.type === "system" && <AlertCircle className="w-5 h-5" />}
                                                    {notification.type === "order" && <Package className="w-5 h-5" />}
                                                    {notification.type === "wallet" && <Wallet className="w-5 h-5" />}
                                                    {notification.type === "support" && <MessageSquare className="w-5 h-5" />}
                                                </span>
                                                <div className="flex-1 space-y-1">
                                                    <p className="text-sm">{notification.message}</p>
                                                    <div className="flex items-center gap-2">
                                                        <Badge 
                                                            variant="outline" 
                                                            className={`${
                                                                notification.priority === "high" 
                                                                    ? "bg-red-500/10 text-red-500 border-red-500/20"
                                                                    : notification.priority === "medium"
                                                                    ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
                                                                    : "bg-green-500/10 text-green-500 border-green-500/20"
                                                            }`}
                                                        >
                                                            {notification.priority === "high" && "بالا"}
                                                            {notification.priority === "medium" && "متوسط"}
                                                            {notification.priority === "low" && "پایین"}
                                                        </Badge>
                                                        <span className="text-xs text-gray-400">
                                                            {moment(notification.createdAt).fromNow()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-4 text-gray-400 text-sm">
                                        هیچ اعلانی وجود ندارد
                                    </div>
                                )}
                            </div>
                        </PopoverContent>
                    </Popover>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <motion.div
                                whileHover={{ scale: 1.05 }} className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-lg border border-white/10"
                            >
                                <p className="text-sm">{walletBalance.toLocaleString()} تومان</p>
                            </motion.div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="center"
                            sideOffset={10}
                            className="w-48 bg-[#1A1525]/30 backdrop-blur-xl border border-white/10 text-white shadow-xl shadow-purple-500/10 rounded-xl"
                        >
                            <DropdownMenuItem 
                                onClick={() => router.push('/dashboard/wallet')}
                                className="cursor-pointer focus:bg-white/10 px-3 py-3 rounded-lg"
                            >
                                <BadgeDollarSign className="w-7 h-7  text-gray-300" />
                                شارژ حساب
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                                onClick={() => router.push('/dashboard/transactions')}
                                className="cursor-pointer px-3 py-3 focus:bg-white/10 rounded-lg"
                            >
                                <List className="w-7 h-7 text-gray-300" />
                                لیست تراکنش
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-lg border border-white/10"
                            >
                                <User className="text-gray-300 w-5 h-5" />
                                <span className="text-white text-sm">
                                    {session?.user ? (session?.user?.phone ? session?.user?.phone : session?.user?.email) : "در حال بروزرسانی"}
                                </span>
                            </motion.div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            sideOffset={10}
                            className="w-48 bg-[#1A1525]/30 backdrop-blur-xl border border-white/10 text-white shadow-xl shadow-purple-500/10 rounded-xl"
                        >
                            <DropdownMenuItem className="cursor-pointer focus:bg-white/10 px-3 py-3 rounded-lg">
                                <Settings className="w-4 h-4 ml-2" />
                                تنظیمات
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer px-3 py-3 focus:bg-white/10 rounded-lg text-red-400">
                                <LogOut className="w-4 h-4 ml-2" />
                                خروج
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                </div>
            </div>
        </div>
    );
};

export default DashboardNavbar;
